import React, { useState } from 'react';
import { FormattedHTMLMessage, useIntl } from 'gatsby-plugin-intl';
import { useStaticQuery, graphql } from 'gatsby';
import Img from 'gatsby-image';
import { css } from '@emotion/core';
import { Desktop, Tablet } from '../../constants/responsive';
import Section from '../../components/Section';
import Container from '../../components/Container';
import { mainColor } from '../../constants/colors';
import { useAppContext } from '../../context/AppContext';
import Arrow from '../../svgs/arrow.svg';
import Button from '../Button';
import { useWindowSize } from 'react-use';

const Step = ({ step, active = false, data, onClick }) => {
  const { width } = useWindowSize();
  let transition = 'all 0.8s ease';
  return (
    <div
      css={css`
        :hover {
          box-shadow: ${active
            ? 'none'
            : `5px 5px 10px 0px #F2F2F2 inset 
            , -5px -5px 10px 0px #F2F2F2 inset`};
        }
        background: ${active ? mainColor : '#989898'};
        width: 100%;
        height: ${active ? 380 : 80}px;
        border-radius: 20px;
        border-bottom-left-radius: ${active ? 0 : 20}px;
        transition: ${transition};
        color: white;
        padding: ${active ? 20 : 26}px 20px;
        display: flex;
        flex-direction: column;
        position: relative;
        overflow: hidden;
        box-shadow: ${active
          ? `5px 5px 40px 0px rgba(103,18,51, 0.7)`
          : 'none'};
        ${Tablet} {
          width: 512px;
          height: ${active ? 330 : 80}px;
          padding: 26px 30px;
        }
        ${Desktop} {
          width: ${active ? 515 : 185}px;
          height: 416px;
          padding: 30px;
        }
      `}
      onClick={onClick}
    >
      <div
        css={css`
          height: 32px;
          position: absolute;
          right: 30px;
          top: 25px;
          transition: ${transition};
          opacity: ${active ? 0 : 1};
          ${Desktop} {
            top: 30px;
          }
        `}
      >
        <Arrow height={32} width={40} />
      </div>
      <h2
        css={css`
          font-weight: 900;
        `}
      >{`${step}.`}</h2>
      <div
        css={css`
          min-width: ${active ? '210px' : '0px'};
          width: ${active ? width - 120 : 0}px;
          position: absolute;
          top: 22px;
          left: 50px;
          transition: ${transition};
          overflow: hidden;
          opacity: ${active ? 1 : 0};
          ${Tablet} {
            top: 70px;
            left: 30px;
            width: ${active ? 260 : 0}px;
          }
          ${Desktop} {
            top: 80px;
            width: ${active ? 220 : 0}px;
          }
        `}
      >
        <h2
          css={css`
            font-size: 20px;
            line-height: 25px;
            margin-bottom: 5px;
            min-width: 210px;
            width: ${width - 120}px;
            ${Tablet} {
              margin-bottom: 16px;
              font-size: 30px;
              line-height: 35px;
              width: 260px;
            }
            ${Desktop} {
              width: 220px;
            }
          `}
        >
          <FormattedHTMLMessage id={`home.how.step.${step}.title`} />
        </h2>
        <h3
          css={css`
            font-size: 14px;
            font-weight: 300;
            line-height: 18px;
            min-width: 210px;
            width: ${width - 120}px;
            br {
              display: none;
            }
            ${Tablet} {
              br {
                display: inline;
              }
              font-size: 22px;
              line-height: 30px;
              width: 260px;
            }
            ${Desktop} {
              width: 220px;
            }
          `}
        >
          <FormattedHTMLMessage id={`home.how.step.${step}.text`} />
        </h3>
      </div>

      <h4
        css={css`
          width: auto;
          position: absolute;
          top: 42px;
          transform: translateY(-50%);
          left: 50px;
          transition: ${transition};
          opacity: ${active ? 0 : 1};
          padding-right: 70px;
          font-size: 14px;
          font-weight: bold;
          line-height: 20px;
          ${Tablet} {
            top: 40px;
            left: 60px;
            font-size: 20px;
            font-weight: normal;
            line-height: 25px;
          }
          ${Desktop} {
            width: 125px;
            top: 80px;
            transform: none;
            left: 30px;
            padding-right: 0px;
          }
        `}
      >
        <FormattedHTMLMessage id={`home.how.step.${step}.title`} />
      </h4>
      <div
        css={css`
          position: absolute;
          transition: ${transition};
          opacity: ${active ? 1 : 0};
          bottom: ${active ? 0 : -250}px;
          left: 50%;
          width: 250px;
          height: 250px;
          transform: translateX(-150px);
          ${Tablet} {
            bottom: ${active ? 0 : -280}px;
            right: 0px;
            width: 280px;
            height: 280px;
            transform: none;
          }
          ${Desktop} {
            bottom: ${active ? 0 : -400}px;
            right: auto;
            left: 145px;
            width: 400px;
            height: 400px;
          }
        `}
      >
        <Img
          fluid={data.childImageSharp.fluid}
          imgStyle={{ objectFit: 'fill' }}
          fadeIn={false}
          loading='eager'
        />
      </div>
    </div>
  );
};

const Gap = () => {
  return (
    <div
      css={css`
        height: 20px;
        ${Desktop} {
          width: 30px;
        }
      `}
    ></div>
  );
};

const HomeHowItWorks = () => {
  const [active, setActive] = useState(1);
  const data = useStaticQuery(graphql`
    query {
      homeStep1En: file(relativePath: { eq: "home-steps/1-en.png" }) {
        childImageSharp {
          fluid(maxWidth: 400) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      homeStep2En: file(relativePath: { eq: "home-steps/2-en.png" }) {
        childImageSharp {
          fluid(maxWidth: 400) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      homeStep3En: file(relativePath: { eq: "home-steps/3-en.png" }) {
        childImageSharp {
          fluid(maxWidth: 400) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      homeStep4En: file(relativePath: { eq: "home-steps/4-en.png" }) {
        childImageSharp {
          fluid(maxWidth: 400) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      homeStep1ZhCn: file(relativePath: { eq: "home-steps/1-zh-cn.png" }) {
        childImageSharp {
          fluid(maxWidth: 400) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      homeStep2ZhCn: file(relativePath: { eq: "home-steps/2-zh-cn.png" }) {
        childImageSharp {
          fluid(maxWidth: 400) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      homeStep3ZhCn: file(relativePath: { eq: "home-steps/3-zh-cn.png" }) {
        childImageSharp {
          fluid(maxWidth: 400) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      homeStep4ZhCn: file(relativePath: { eq: "home-steps/4-zh-cn.png" }) {
        childImageSharp {
          fluid(maxWidth: 400) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      homeStep1ZhHk: file(relativePath: { eq: "home-steps/1-zh-hk.png" }) {
        childImageSharp {
          fluid(maxWidth: 400) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      homeStep2ZhHk: file(relativePath: { eq: "home-steps/2-zh-hk.png" }) {
        childImageSharp {
          fluid(maxWidth: 400) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      homeStep3ZhHk: file(relativePath: { eq: "home-steps/3-zh-hk.png" }) {
        childImageSharp {
          fluid(maxWidth: 400) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      homeStep4ZhHk: file(relativePath: { eq: "home-steps/4-zh-hk.png" }) {
        childImageSharp {
          fluid(maxWidth: 400) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
    }
  `);

  const intl = useIntl();
  const { footerFormRef } = useAppContext();
  const imageLocale = intl.locale
    .split('-')
    .map((code) => code[0].toUpperCase() + code[1])
    .join('');
  return (
    <Section>
      <Container>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;
            // margin-top: 100px;
            ${Tablet} {
              text-align: center;
              align-items: center;
            }
            ${Desktop} {
              // margin-top: 150px;
            }
          `}
        >
          {/* <h5
            css={css`
              margin-bottom: 10px;
            `}
          >
            <strong>
              <FormattedHTMLMessage id='home.how.subtitle' />
            </strong>
          </h5>
          <h1>
            <FormattedHTMLMessage id='home.how.title' />
          </h1> */}
          <div
            css={css`
              color: #161616;
              align-self: center;
              font-size: 24px;
              font-weight: bold;
              line-height: 1.33;
              margin-bottom: 30px;
              ${Tablet} {
                font-size: 36px;
                font-weight: 500;
                line-height: 1.33;
                align-self: center;
                margin-bottom: 33px;
              }
              ${Desktop} {
                font-size: 36px;
                font-weight: bold;
                line-height: normal;
                align-self: flex-start;
                margin-bottom: 33px;
              }
            `}
          >
            {intl.formatMessage({
              id: 'individuals.longTerm-assetAllocation.how-it-work',
            })}
          </div>
        </div>

        <div
          css={css`
            display: flex;
            flex-direction: column;
            ${Tablet} {
              align-items: center;
            }
            ${Desktop} {
              flex-direction: row;
            }
          `}
        >
          {[1, 0, 2, 0, 3, 0, 4].map((step, index) => {
            if (step > 0) {
              return (
                <Step
                  key={step}
                  step={step}
                  data={data[`homeStep${step}${imageLocale}`]}
                  active={active === step}
                  onClick={() => {
                    setActive(step);
                  }}
                />
              );
            } else {
              return <Gap key={`gap-${step + index}`} />;
            }
          })}
        </div>
        {/* <div
          css={css`
            display: flex;
            justify-content: center;
            margin-top: 30px;
          `}
        >
          <Button scrollToRef={footerFormRef}>
            <FormattedHTMLMessage id="home.how.button" />
          </Button>
        </div> */}
      </Container>
    </Section>
  );
};

export default HomeHowItWorks;
